import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";
import { useActiveSession } from "./useTimerSession";
import type { UseQueryResult } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DistractionRow {
  status: "inactive" | "active" | "distracted";
  type?: string;
  url?: string | null;
  createdAt?: string | Date | null;
  sessionId?: string;
}
export interface DistractionsByType {
  phone: DistractionRow | null;
  /** The browser, reported by the Chrome extension. */
  website: DistractionRow | null;
  /** The Swift desktop client. Present before that client ships. */
  desktop: DistractionRow | null;
}
export interface UseDistractionsOptions {
  refetchInterval?: number | false | (() => number | false);
  /**
   * Keep the polling interval alive while the tab is hidden/blurred. TanStack
   * pauses `refetchInterval` when the window loses focus unless this is `true`.
   * The connection-status freshness floor relies on this so distraction badges
   * don't flip "Off" while the tab is in the background.
   */
  refetchIntervalInBackground?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  enabled?: boolean;
  /**
   * Called whenever the query settles (succeeds or errors).
   * Provided as a standalone option because TanStack v5 removed
   * `onSettled` from useQuery options.
   */
  onSettled?: (data: DistractionsByType | null | undefined, error: Error | null) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches distraction data for the current active session.
 *
 * Polls with a jittered interval (~60s +/- 10s) when a session is active to
 * desync from other polling and prevent query storms. Automatically scopes
 * the cache to the current session ID to avoid cross-session leakage.
 */
export function useDistractions(options?: UseDistractionsOptions): UseQueryResult<DistractionsByType | null, Error> {
  const apiFetcher = useApiFetcher();
  const {
    data: activeSession
  } = useActiveSession();
  const sessionId = activeSession?.session?.id ?? null;
  const BASE_INTERVAL_MS = 60_000;
  const JITTER_MS = 10_000;
  const BURST_AFTER_START_MS = 180_000;
  const BURST_POLL_MS = 5_000;

  // Jitter desyncs polling across clients but must be stable across interval
  // evaluations: TanStack resets its timer whenever the callback returns a
  // different value, so per-evaluation jitter starves any interval longer
  // than the host component's re-render cadence.
  const burstPollMsRef = useRef(BURST_POLL_MS + Math.random() * 800);
  const steadyPollMsRef = useRef(BASE_INTERVAL_MS + (Math.random() * 2 - 1) * JITTER_MS);
  const defaultRefetchInterval = (): number | false => {
    if (!sessionId) {
      return false;
    }
    const startedAt = activeSession?.session?.startedAt;
    if (startedAt) {
      const age = Date.now() - Date.parse(startedAt);
      if (age >= 0 && age < BURST_AFTER_START_MS) {
        return burstPollMsRef.current;
      }
    }
    return steadyPollMsRef.current;
  };
  const query = useQuery<DistractionsByType | null, Error>({
    queryKey: QUERY_KEYS.distractions(sessionId ?? undefined),
    queryFn: async () => {
      const json = await apiFetcher<Partial<DistractionsByType>>("/api/v1/sessions/distraction");
      return {
        phone: json.phone ?? null,
        website: json.website ?? null,
        desktop: json.desktop ?? null
      };
    },
    refetchInterval: options?.refetchInterval ?? defaultRefetchInterval,
    refetchIntervalInBackground: options?.refetchIntervalInBackground ?? false,
    staleTime: options?.staleTime ?? 30_000,
    gcTime: options?.gcTime ?? 60_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    refetchOnReconnect: options?.refetchOnReconnect ?? false,
    enabled: (options?.enabled ?? true) && Boolean(sessionId),
    refetchOnMount: true
  });
  useEffect(() => {
    if (options?.onSettled) {
      options.onSettled(query.data, query.error as Error ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, query.error]);
  return query;
}